/**
 * Clients: the agency's home screen. Search, add a client, and a table of
 * clients (initials, name, website, date created) with a totals row. A row
 * opens that client's dashboards.
 */

import { useMemo, useState, type FormEvent, type ReactNode } from 'react';

import { formatValue } from '../reports/format.ts';
import type { Catalogue, Client } from '../reports/reports-api.ts';
import { columnKey, useClientColumns, type ClientColumn } from './client-columns.ts';
import { ClientColumnsPicker, ClientPeriodSelect } from './ClientColumnsPicker.tsx';
import { clientsApi } from './clients-api.ts';
import { GettingStarted } from './GettingStarted.tsx';
import { go } from './routes.ts';

const created = new Intl.DateTimeFormat('en-ZA', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const AVATAR_TONES = 6;

export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? words[0]?.[1] ?? '')).toUpperCase();
}

/** A stable tone per client, so the same client always has the same avatar colour. */
export function toneOf(id: string): number {
  return [...id].reduce((sum, char) => sum + char.charCodeAt(0), 0) % AVATAR_TONES;
}

function AddClientDialog({
  organisationId,
  onClose,
  onAdded,
}: {
  organisationId: string;
  onClose: () => void;
  onAdded: (client: Client) => void;
}): ReactNode {
  const [name, setName] = useState('');
  const [website, setWebsite] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setProblem(null);
    try {
      onAdded(
        await clientsApi.create(organisationId, {
          name: name.trim(),
          website: website.trim() || null,
          timezone: 'Africa/Johannesburg',
        }),
      );
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'The client could not be added.');
      setBusy(false);
    }
  };
  return (
    <div
      className="rc-scrim"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <form
        className="rc ag-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ag-add-title"
        onSubmit={(event) => void submit(event)}
      >
        <h2 id="ag-add-title">Add client</h2>
        {problem && (
          <p className="rh-access-msg rh-access-error" role="alert">
            {problem}
          </p>
        )}
        <label className="rs-field">
          Client name
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={200}
            required
            autoFocus
          />
        </label>
        <label className="rs-field">
          Website
          <input
            value={website}
            onChange={(event) => setWebsite(event.target.value)}
            placeholder="example.co.za"
          />
        </label>
        <div className="rc-actions">
          <button type="button" className="btn secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn" disabled={busy || !name.trim()}>
            {busy ? 'Adding…' : 'Add client'}
          </button>
        </div>
      </form>
    </div>
  );
}

export function ClientsPage({
  organisationId,
  clients,
  catalogue,
  onChanged,
}: {
  organisationId: string;
  clients: Client[] | null;
  catalogue: Catalogue | null;
  onChanged: () => void;
}): ReactNode {
  const table = useClientColumns(organisationId, catalogue);
  const metricOf = (column: ClientColumn) =>
    catalogue?.sources
      .find((source) => source.key === column.source)
      ?.metrics.find((metric) => metric.key === column.metric);
  const cell = (value: number | null, column: ClientColumn) =>
    table.isLoading ? '…' : formatValue(value, metricOf(column)?.format ?? 'number');
  const [search, setSearch] = useState('');
  const [ascending, setAscending] = useState(true);
  const [adding, setAdding] = useState(false);
  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    const found = (clients ?? []).filter(
      (client) =>
        !term || client.name.toLowerCase().includes(term) || (client.website ?? '').includes(term),
    );
    return found.sort((a, b) => (ascending ? 1 : -1) * a.name.localeCompare(b.name));
  }, [clients, search, ascending]);

  return (
    <section className="ag-page" aria-labelledby="ag-clients-title">
      <header className="ag-head">
        <h1 id="ag-clients-title">Clients</h1>
        <span className="ag-spacer" />
        <label className="ag-search">
          <span className="sr-only">Search clients</span>
          <input
            type="search"
            placeholder="Search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <ClientPeriodSelect preset={table.preset} onChange={table.setPreset} />
        {catalogue && (
          <ClientColumnsPicker
            catalogue={catalogue}
            columns={table.columns}
            onChange={table.setColumns}
          />
        )}
        <button type="button" className="btn" onClick={() => setAdding(true)}>
          Add Client
        </button>
      </header>
      <div className="ag-body">
        {clients !== null && (
          <GettingStarted
            organisationId={organisationId}
            clients={clients}
            onAddClient={() => setAdding(true)}
          />
        )}
        {clients !== null && clients.length === 0 ? (
          <div className="ag-empty">
            <h2>Add your first client</h2>
            <p>Every dashboard, report and data source belongs to a client.</p>
            <button type="button" className="btn" onClick={() => setAdding(true)}>
              Add Client
            </button>
          </div>
        ) : (
          <div className="ag-table-card">
            <p className="ag-table-meta">
              {clients === null ? 'Loading…' : `Showing ${rows.length} of ${clients.length} Rows`}
            </p>
            <table className="ag-table">
              <thead>
                <tr>
                  <th scope="col" aria-sort={ascending ? 'ascending' : 'descending'}>
                    <button
                      type="button"
                      className="ag-sort"
                      onClick={() => setAscending(!ascending)}
                    >
                      Client <span aria-hidden="true">{ascending ? '▲' : '▼'}</span>
                    </button>
                  </th>
                  <th scope="col">Date Created</th>
                  {table.columns.map((column) => (
                    <th scope="col" key={columnKey(column)} className="ag-num">
                      {metricOf(column)?.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((client) => (
                  <tr
                    key={client.id}
                    className="ag-row"
                    onClick={() =>
                      go({
                        area: 'client',
                        clientId: client.id,
                        tab: 'dashboards',
                        documentId: null,
                      })
                    }
                  >
                    <td>
                      <a
                        className="ag-client-cell"
                        href={`#/client/${client.id}/dashboards`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        <span
                          className={`ag-avatar ag-tone-${toneOf(client.id)}`}
                          aria-hidden="true"
                        >
                          {initialsOf(client.name)}
                        </span>
                        <span>
                          <strong>{client.name}</strong>
                          {client.website && <small>{client.website}</small>}
                        </span>
                      </a>
                    </td>
                    <td>{created.format(new Date(client.created_at))}</td>
                    {table.columns.map((column) => (
                      <td key={columnKey(column)} className="ag-num">
                        {cell(table.valueOf(client.name, column), column)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" className="ag-totals">
                    Totals
                  </th>
                  <td>{rows.length}</td>
                  {table.columns.map((column) => (
                    <td key={columnKey(column)} className="ag-num">
                      {cell(table.totalOf(column), column)}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
      {adding && (
        <AddClientDialog
          organisationId={organisationId}
          onClose={() => setAdding(false)}
          onAdded={(client) => {
            setAdding(false);
            onChanged();
            go({ area: 'client', clientId: client.id, tab: 'dashboards', documentId: null });
          }}
        />
      )}
    </section>
  );
}
